import { useEffect, useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect, useDispatch } from "react-redux";
import { updateSnapShot } from "../../../../../action/masters/snapShotActionCreator";
import {
  cloneDeep,
  baseURL,
  urlRegex,
} from "../../../../../constants/commonConstants";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import {
  ISnapShotEdit,
  ISnapShotData,
} from "../../../../../models/master/snapShot";
import { setSnapShot } from "../../../../../store/slices/master/snapShotSlice";
import LoaderSpinner from "../../../../../common-components/loader";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import Joi from "joi";
import ReactModal from "react-modal";
import CropComponent from "../../../../../common-components/cropComponent";
import { validateFields } from "../../../../../helper/joi-validation";

const EditSnapShot = (props: ISnapShotEdit) => {
  const dispatch = useDispatch();
  const { updateSnapShot, snapShot, getAllSnapShots, setShowEdit } = props;

  // states

  const [data, setData] = useState<ISnapShotData>(cloneDeep(snapShot) || {});
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState<boolean>(false);
  const [croppedImage, setCroppedImage] = useState<File | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);

  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setData({ ...data, [event.target.name]: event.target.value });
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setImageName(selectedFile.name);
      const fileType = selectedFile.type;
      const validFileTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/ico",
        "image/svg+xml",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an image.",
        });
      } else {
        // setData({ ...data, [event.target.name]: selectedFile });
        // const fileURL = URL.createObjectURL(selectedFile);
        // setImagePreview(fileURL);
        setData({ ...data, file: selectedFile });
        setShowCropModal(true);
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  useEffect(() => {
    if (snapShot) {
      let _snapShot = cloneDeep(snapShot);
      setData(_snapShot);
      setFileName(_snapShot.image);
      if (_snapShot.image) {
        const imageUrl = baseURL + _snapShot.image;
        setImageName(_snapShot.image);
        setImagePreview(imageUrl);
      }
    }
  }, [snapShot]);

  // setting sweet alert dynamically
  const setAlert = (type: string) => {
    const alertData = getAlertData(type);
    if (alertData) {
      if (data) {
        setAlertType(alertData.type);
        setAlertTitle(alertData.title);
        setShowAlert(true);
      } else {
        setShowAlert(false);
      }
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      if (data.file) {
        getAllSnapShots();
        setShowEdit(false);
      } else {
        setShowEdit(true);
        setShowAlert(false);
      }
    }
  };

  const schema = Joi.object({
    url: Joi.string()
      .optional()
      .allow(null)
      .regex(urlRegex)
      .label("Enter Valid URL"),
    file: Joi.any().when("isFile", {
      is: true,
      then: Joi.any().optional(),
      otherwise: Joi.any().required().label("File is required"),
    }),
  });

  const validationData = {
    ...data,
    isFile: Boolean(data.file),
  };

  const onUpdate = async () => {
    const validationErrors = validateFields(validationData, schema);
    if (validationErrors === undefined) {
      const formData = new FormData();
      formData.append("id", data.id?.toString() || "");
      formData.append("file", data.file);
      formData.append("isActive", "true");
      formData.append("url", data?.url ?? "");
      if (!croppedImage && fileName === undefined) {
        setAlert(alertConstants.fileRequired);
      } else {
        setLoading(true);
        setIsDisabled(true);
        try {
          await updateSnapShot(formData, data.id ?? 0, setAlert);
        } finally {
          setLoading(false);
          setIsDisabled(false);
        }
      }
    } else {
      setErrors(validationErrors);
    }
  };

  const handleCropComplete = (croppedFile: File) => {
    setCroppedImage(croppedFile);
    setData({ ...data, file: croppedFile });

    const fileURL = URL.createObjectURL(croppedFile);
    setImagePreview(fileURL);

    setFileName(croppedFile.name);

    setShowCropModal(false);
  };

  useEffect(() => {
    return () => {
      dispatch(setSnapShot(undefined));
    };
  }, []);

  return (
    <>
      {data && (
        <>
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
              Update Snapshot
            </h1>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={() => setShowEdit(false)}
            ></button>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"URL"}
                  onChange={onChange}
                  isMandatory={false}
                  value={data.url}
                  name={"url"}
                  maxLength={50}
                />
                {errors?.url && <div className="text-danger">{errors.url}</div>}
              </div>
              <div className="col-md-6 form-group">
                <label className="profile_details_text">
                  Snapshot Filter Image
                </label>
                <div className="inputfile-flex">
                  {imagePreview && (
                    <div className="inputfile-img">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ width: "50px", height: "50px" }}
                      />
                    </div>
                  )}
                  <div className="inputfile-wrap">
                    <input
                      type="file"
                      name="file"
                      id="image"
                      className="form-control d-none"
                      required
                      onChange={onFileChange}
                      accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp, image/x-icon, text/html"
                    />
                    <div className="inputfile">
                      <label htmlFor="image">Choose File</label>
                      <span>
                        {imageName
                          ? imageName.trim().split("/").pop()
                          : "No File Choosen"}
                      </span>
                    </div>
                  </div>
                </div>
                {errors?.file && (
                  <div className="text-danger">{errors.file}</div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onUpdate}
                disabled={isDisabled}
              >
                Update
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={() => setShowEdit(false)}
                disabled={isDisabled}
              >
                Close
              </button>
            </div>
          </div>

          <ReactModal
            isOpen={showCropModal}
            onRequestClose={() => setShowCropModal(false)}
            contentLabel="Crop Image"
            ariaHideApp={false}
            shouldCloseOnOverlayClick={false}
            style={{
              overlay: {
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 1050,
              },
              content: {
                width: "fit-content",
                margin: "auto",
                borderRadius: "10px",
                zIndex: 1100,
                position: "relative",
              },
            }}
          >
            {data?.id && (
              <CropComponent
                onCropComplete={handleCropComplete}
                onClose={() => setShowCropModal(false)}
                initialImage={data.file}
              />
            )}
          </ReactModal>

          <SweetAlert
            show={showAlert}
            success={false}
            type={alertType}
            title={alertTitle}
            onConfirm={() => onConfirm()}
            showCancel={showAlertCancel}
            onCancel={() => {
              setShowAlert(false);
              setShowAlertCancel(false);
            }}
            cancelBtnText={"No"}
            confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
          ></SweetAlert>
          {loading && <LoaderSpinner />}
        </>
      )}
    </>
  );
};

const mapDispatchToProps = {
  updateSnapShot: (data: any, id: number, callBack: Function) =>
    updateSnapShot(data, id, callBack),
};

function mapStateToProps(state: any) {
  return {};
}

export default connect(mapStateToProps, mapDispatchToProps)(EditSnapShot);
